import React, { useEffect, useState, FormEvent } from 'react';
import { Modal } from 'react-bootstrap';
import toast from '../../components/toast';
import groupServices from '../../services/groupServices';
import assetGroupService from '../../services/assetGroupService';
import { idOf, mergeById } from './pickerUtils';

interface Props {
  show: boolean;
  onClose: () => void;
  onCreated?: () => void;
  organisationId?: string;
}

const CreateAssetGroupModal: React.FC<Props> = ({ show, onClose, onCreated, organisationId }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sensorboxList, setSensorboxList] = useState<any[]>([]);
  const [selectedSensorboxes, setSelectedSensorboxes] = useState<any[]>([]);
  const [assetCandidates, setAssetCandidates] = useState<any[]>([]); // sensorbox groups as assets
  const [selectedAssets, setSelectedAssets] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [processing, setProcessing] = useState(false);
  
  // Search states for the new design
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [sensorboxSearchTerm, setSensorboxSearchTerm] = useState('');


  useEffect(() => {
    if (show) {
      fetchSensorboxes();
      fetchAssetCandidates();
    } else {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const clear = () => {
    setName('');
    setDescription('');
    setSelectedSensorboxes([]);
    setSelectedAssets([]);
    setErrors({});
    setAssetSearchTerm('');
    setSensorboxSearchTerm('');
  };

  const fetchSensorboxes = async (currentSelected: any[] = []) => {
    try {
      const resp = await groupServices.getSensorboxesForGroup(organisationId);
      setSensorboxList(mergeById(resp?.data || [], currentSelected));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const fetchAssetCandidates = async (currentSelected: any[] = []) => {
    try {
      const resp = await groupServices.getAllGroups({ keyword: '', orgid: organisationId });
      setAssetCandidates(mergeById(resp?.data?.groups || [], currentSelected));
    } catch (error: any) {
      // non-blocking
    }
  };

  const validate = () => {
    const e: any = {};
    if (!name?.trim()) e.name = 'Name is required';
    if (selectedAssets.length == 0 && selectedSensorboxes.length == 0) e.assets = 'Select at least one Asset or Sensorbox';
    setErrors(e);
    return Object.keys(e).length > 0;
  };

  const onSubmit = async (evt: FormEvent) => {
    evt.preventDefault();
    if (validate()) return;
    try {
      setProcessing(true);
      const payload = {
        name: name.trim(),
        description: description?.trim() || undefined,
        sensorboxes: selectedSensorboxes.map(s => idOf(s)),
        assets: selectedAssets.map((a: any) => idOf(a)),
      };
      await assetGroupService.createAssetGroup(payload, organisationId);
      setProcessing(false);
      onClose();
      onCreated && onCreated();
    } catch (error: any) {
      setProcessing(false);
      toast.error(error?.response?.data || error?.message);
    }
  };

  const removeSelected = (id: string, type: 'sensorbox' | 'asset') => {
    const selectedId = idOf(id);
    if (type === 'sensorbox') setSelectedSensorboxes(prev => prev.filter(s => idOf(s) !== selectedId));
    if (type === 'asset') setSelectedAssets(prev => prev.filter(s => idOf(s) !== selectedId));
  };

  const addAsset = (asset: any) => {
    if (!selectedAssets.find(a => idOf(a) === idOf(asset))) {
      setSelectedAssets(prev => [...prev, asset]);
    }
  };

  const addSensorbox = (sensorbox: any) => {
    if (!selectedSensorboxes.find(s => idOf(s) === idOf(sensorbox))) {
      setSelectedSensorboxes(prev => [...prev, sensorbox]);
    }
  };

  const selectedAssetIds = new Set(selectedAssets.map(idOf));
  const selectedSensorboxIds = new Set(selectedSensorboxes.map(idOf));
  const assetKeyword = assetSearchTerm.toLowerCase();
  const sensorboxKeyword = sensorboxSearchTerm.toLowerCase();

  const filteredAssets = assetCandidates.filter(asset =>
    asset.name?.toLowerCase().includes(assetKeyword) &&
    !selectedAssetIds.has(idOf(asset))
  );

  const filteredSensorboxes = sensorboxList.filter(sensorbox => {
    const name = sensorbox.sensor_box_name?.toLowerCase() || '';
    const alias = sensorbox.alias?.toLowerCase() || '';
    return (name.includes(sensorboxKeyword) || alias.includes(sensorboxKeyword)) &&
      !selectedSensorboxIds.has(idOf(sensorbox));
  });

  return (
    <Modal show={show} onHide={onClose} backdrop="static" className="create-group-modal">
      <Modal.Header className="py-2 px-3" closeButton>
        Create Asset Group
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={onSubmit}>
          <div className="mb-2">
            <label>Name <span className="mand-star">*</span></label>
            <input
              className="form-control"
              type="text"
              placeholder="Enter name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
            />
            <div className="error-msg">{errors?.name}</div>
          </div>

          <div className="mb-2">
            <label>Description</label>
            <textarea
              className="form-control"
              placeholder="Enter description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="mb-3">
            <label>Assets</label>
            <div className="border rounded p-2">
              <div className="row">
                {/* Left side - Available assets */}
                <div className="col-6">
                  <div className="mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search"
                      value={assetSearchTerm}
                      onChange={(e) => setAssetSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="border rounded" style={{ height: '200px', overflowY: 'auto' }}>
                    {filteredAssets.map((asset) => (
                      <div 
                        key={idOf(asset)} 
                        className="d-flex align-items-center p-2 border-bottom cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => addAsset(asset)}
                      >
                        <span className="text-success mr-2">+</span>
                        {asset.name}
                      </div>
                    ))}
                    {filteredAssets.length === 0 && (
                      <div className="text-muted text-center p-3">
                        {assetSearchTerm ? 'No assets found' : 'No available assets'}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Right side - Selected assets */}
                <div className="col-6">
                  <div className="text-muted mb-2">Selected Assets</div>
                  <div className="border rounded" style={{ height: '200px', overflowY: 'auto' }}>
                    {selectedAssets.map((asset) => (
                      <div key={idOf(asset)} className="d-flex align-items-center bg-light rounded p-2 mb-2 mx-1">
                        <span className="flex-grow-1">{asset.name}</span>
                        <i 
                          className="fa fa-close text-muted cursor-pointer" 
                          onClick={() => removeSelected(idOf(asset), 'asset')}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    ))}
                    {selectedAssets.length === 0 && (
                      <div className="text-muted text-center p-3">
                        No assets selected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label>Sensorboxes</label>
            <div className="border rounded p-2">
              <div className="row">
                {/* Left side - Available sensorboxes */}
                <div className="col-6">
                  <div className="mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search"
                      value={sensorboxSearchTerm}
                      onChange={(e) => setSensorboxSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="border rounded" style={{ height: '200px', overflowY: 'auto' }}>
                    {filteredSensorboxes.map((sensorbox) => (
                      <div 
                        key={idOf(sensorbox)} 
                        className="d-flex align-items-center p-2 border-bottom cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => addSensorbox(sensorbox)}
                      >
                        <span className="text-success mr-2">+</span>
                        <span>{sensorbox.sensor_box_name}</span>
                        {sensorbox?.alias && (
                          <span className="text-truncate">&nbsp;&nbsp;( {sensorbox.alias} )</span>
                        )}
                      </div>
                    ))}
                    {filteredSensorboxes.length === 0 && (
                      <div className="text-muted text-center p-3">
                        {sensorboxSearchTerm ? 'No sensorboxes found' : 'No available sensorboxes'}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Right side - Selected sensorboxes */}
                <div className="col-6">
                  <div className="text-muted mb-2">Selected Sensorboxes</div>
                  <div className="border rounded" style={{ height: '200px', overflowY: 'auto' }}>
                    {selectedSensorboxes.map((sensorbox) => (
                      <div key={idOf(sensorbox)} className="d-flex align-items-center bg-light rounded p-2 mb-2 mx-1">
                        <span className="flex-grow-1">{sensorbox.sensor_box_name}</span>
                        <i 
                          className="fa fa-close text-muted cursor-pointer" 
                          onClick={() => removeSelected(idOf(sensorbox), 'sensorbox')}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    ))}
                    {selectedSensorboxes.length === 0 && (
                      <div className="text-muted text-center p-3">
                        No sensorboxes selected
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="error-msg">{errors?.assets}</div>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-sm rounded-0"
              style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92', color: '#fff' }}
              disabled={processing}
            >
              Create
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateAssetGroupModal;

